import { collection, query, where, getDocs, doc, updateDoc, addDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../firebase';
import { TrafficTicket, TicketStatus } from '../types';

export interface PaymentReminder {
  id: string;
  ticketId: string;
  ticketCode: string;
  violatorEmail: string;
  violatorName: string;
  fineAmount: number;
  dueDate: Date;
  reminderType: 'due_soon' | 'overdue' | 'critical';
  daysOverdue: number;
  isSent: boolean;
  createdAt: Date;
  sentAt?: Date;
}

export class PaymentReminderService {
  // Check for unpaid tickets and create reminders
  static async checkAndCreateReminders(): Promise<PaymentReminder[]> {
    const reminders: PaymentReminder[] = [];
    
    try {
      // First get all viewer accounts to see which tickets are linked to viewers
      const viewerAccountsQuery = query(collection(db, 'viewerAccounts'));
      const viewerAccountsSnapshot = await getDocs(viewerAccountsQuery);
      
      // Collect all linked ticket codes from all viewer accounts
      const allLinkedTicketCodes = new Set<string>();
      const viewerTicketMap = new Map<string, string[]>(); // email -> ticket codes
      
      viewerAccountsSnapshot.forEach((viewerDoc) => {
        const viewerData = viewerDoc.data();
        const linkedTicketCodes = viewerData.linkedTicketCodes || [];
        const viewerEmail = viewerDoc.id;
        
        // Add both formats of each code (with and without TKT- prefix)
        linkedTicketCodes.forEach((code: string) => {
          allLinkedTicketCodes.add(code);
          if (!code.startsWith('TKT-') && code.match(/^\d{4}-/)) {
            allLinkedTicketCodes.add('TKT-' + code);
          }
        });
        
        viewerTicketMap.set(viewerEmail, linkedTicketCodes);
      });
      
      if (allLinkedTicketCodes.size === 0) {
        return []; // No tickets linked to any viewers
      }
      
      // Now get only the unpaid tickets that are linked to viewers
      const now = new Date();
      
      // Firestore 'in' queries are limited to 10 items, so we batch if needed
      const ticketCodeArray = Array.from(allLinkedTicketCodes);
      
      for (let i = 0; i < ticketCodeArray.length; i += 10) {
        const batch = ticketCodeArray.slice(i, i + 10);
        
        const unpaidTicketsQuery = query(
          collection(db, 'trafficTickets'),
          where('ticketCode', 'in', batch),
          where('status', '==', TicketStatus.ISSUED)
        );
        
        const querySnapshot = await getDocs(unpaidTicketsQuery);
        
        for (const doc of querySnapshot.docs) {
          const ticket = doc.data() as any;
          const ticketData: TrafficTicket = {
            ...ticket,
            id: doc.id,
            createdAt: (ticket.createdAt as any)?.toDate?.() || new Date(),
          } as TrafficTicket;
          
          // Calculate days overdue (assuming 30 days payment period)
          const daysSinceIssued = Math.floor(
            (now.getTime() - ticketData.createdAt.getTime()) / (1000 * 60 * 60 * 24)
          );
          
          // Determine reminder type
          let reminderType: 'due_soon' | 'overdue' | 'critical' | null = null;
          let shouldCreateReminder = false;
          
          if (daysSinceIssued >= 25 && daysSinceIssued < 30) {
            reminderType = 'due_soon';
            shouldCreateReminder = true;
          } else if (daysSinceIssued >= 30 && daysSinceIssued < 45) {
            reminderType = 'overdue';
            shouldCreateReminder = true;
          } else if (daysSinceIssued >= 45) {
            reminderType = 'critical';
            shouldCreateReminder = true;
          }
          
          if (shouldCreateReminder && reminderType) {
            // Find which viewer(s) this ticket is linked to
            const ticketCode = ticketData.ticketCode || doc.id.slice(0, 8);
            const linkedViewers: string[] = [];
            
            viewerTicketMap.forEach((ticketCodes, viewerEmail) => {
              if (ticketCodes.includes(ticketCode) || 
                  ticketCodes.includes('TKT-' + ticketCode) ||
                  (ticketCode.startsWith('TKT-') && ticketCodes.includes(ticketCode.replace('TKT-', '')))) {
                linkedViewers.push(viewerEmail);
              }
            });
            
            // Create reminders for each linked viewer
            for (const viewerEmail of linkedViewers) {
              const dueDate = new Date(ticketData.createdAt);
              dueDate.setDate(dueDate.getDate() + 30);
              
              const reminder: PaymentReminder = {
                id: '', // Will be set by Firestore
                ticketId: doc.id,
                ticketCode: ticketData.ticketCode || doc.id.slice(0, 8),
                violatorEmail: viewerEmail,
                violatorName: ticketData.violatorName,
                fineAmount: ticketData.fineAmount || 0,
                dueDate,
                reminderType,
                daysOverdue: Math.max(0, daysSinceIssued - 30),
                isSent: false,
                createdAt: now,
              };
              
              // Check if reminder already exists for this viewer and ticket
              const existingReminderQuery = query(
                collection(db, 'paymentReminders'),
                where('ticketId', '==', doc.id),
                where('violatorEmail', '==', viewerEmail),
                where('reminderType', '==', reminderType)
              );
              
              const existingSnapshot = await getDocs(existingReminderQuery);
              
              if (existingSnapshot.empty) {
                // Create new reminder
                const docRef = await addDoc(collection(db, 'paymentReminders'), {
                  ...reminder,
                  createdAt: serverTimestamp(),
                });
                
                reminder.id = docRef.id;
                reminders.push(reminder);
                
                // Create notification for the user
                await this.createPaymentReminderNotification(reminder);
              }
            }
          }
        }
      }
      
      return reminders;
    } catch (error) {
      console.error('Error checking payment reminders:', error);
      return [];
    }
  }
  
  // Create notification for payment reminder
  static async createPaymentReminderNotification(reminder: PaymentReminder): Promise<void> {
    try {
      const notificationData = {
        title: this.getReminderTitle(reminder.reminderType),
        message: this.getReminderMessage(reminder),
        type: 'payment',
        priority: this.getReminderPriority(reminder.reminderType),
        toUserRole: 'viewer',
        toUserId: null, // Will be filtered by email in the frontend
        isRead: false,
        data: {
          ticketId: reminder.ticketId,
          ticketCode: reminder.ticketCode,
          amount: reminder.fineAmount,
          dueDate: reminder.dueDate,
          daysOverdue: reminder.daysOverdue,
          reminderType: reminder.reminderType,
        },
        createdAt: serverTimestamp(),
      };
      
      await addDoc(collection(db, 'notifications'), notificationData);
    } catch (error) {
      console.error('Error creating payment reminder notification:', error);
    }
  }
  
  // Get reminder title based on type
  private static getReminderTitle(type: 'due_soon' | 'overdue' | 'critical'): string {
    switch (type) {
      case 'due_soon':
        return 'Payment Due Soon';
      case 'overdue':
        return 'Payment Overdue';
      case 'critical':
        return 'Urgent: Payment Required';
      default:
        return 'Payment Reminder';
    }
  }
  
  // Get reminder message based on type
  private static getReminderMessage(reminder: PaymentReminder): string {
    const { ticketCode, fineAmount, dueDate, daysOverdue, reminderType } = reminder;
    
    switch (reminderType) {
      case 'due_soon':
        return `Your ticket ${ticketCode} with a fine of EC$${fineAmount} is due on ${dueDate.toLocaleDateString()}. Please make your payment soon to avoid penalties.`;
      case 'overdue':
        return `Your ticket ${ticketCode} with a fine of EC$${fineAmount} is overdue by ${daysOverdue} days. Please make your payment immediately to avoid further consequences.`;
      case 'critical':
        return `URGENT: Your ticket ${ticketCode} with a fine of EC$${fineAmount} is critically overdue by ${daysOverdue} days. Immediate payment is required to avoid legal action.`;
      default:
        return `Please pay your ticket ${ticketCode} with a fine of EC$${fineAmount}.`;
    }
  }
  
  // Get notification priority based on reminder type
  private static getReminderPriority(type: 'due_soon' | 'overdue' | 'critical'): 'low' | 'medium' | 'high' | 'urgent' {
    switch (type) {
      case 'due_soon':
        return 'medium';
      case 'overdue':
        return 'high';
      case 'critical':
        return 'urgent';
      default:
        return 'medium';
    }
  }
  
  // Mark reminder as sent
  static async markReminderAsSent(reminderId: string): Promise<void> {
    try {
      await updateDoc(doc(db, 'paymentReminders', reminderId), {
        isSent: true,
        sentAt: serverTimestamp(),
      });
    } catch (error) {
      console.error('Error marking reminder as sent:', error);
    }
  }
  
  // Get reminders for a specific user
  static async getUserReminders(email: string): Promise<PaymentReminder[]> {
    try {
      const remindersQuery = query(
        collection(db, 'paymentReminders'),
        where('violatorEmail', '==', email),
        where('isSent', '==', false)
      );
      
      const querySnapshot = await getDocs(remindersQuery);
      const reminders: PaymentReminder[] = [];
      
      querySnapshot.forEach((doc) => {
        const data = doc.data();
        reminders.push({
          id: doc.id,
          ...data,
          dueDate: data.dueDate?.toDate ? data.dueDate.toDate() : (data.dueDate as any)?.toDate?.() || new Date(),
          createdAt: (data.createdAt as any)?.toDate?.() || new Date(),
          sentAt: (data.sentAt as any)?.toDate?.() || undefined,
        } as PaymentReminder);
      });
      
      return reminders.sort((a, b) => b.createdAt.getTime() - a.createdAt.getTime());
    } catch (error) {
      console.error('Error getting user reminders:', error);
      return [];
    }
  }
  
  // Get reminders for a specific ticket
  static async getRemindersForTicket(ticketId: string): Promise<PaymentReminder[]> {
    try {
      const remindersQuery = query(
        collection(db, 'paymentReminders'),
        where('ticketId', '==', ticketId),
        where('isSent', '==', false)
      );
      
      const querySnapshot = await getDocs(remindersQuery);
      const reminders: PaymentReminder[] = [];
      
      querySnapshot.forEach((doc) => {
        const data = doc.data();
        reminders.push({
          id: doc.id,
          ...data,
          dueDate: data.dueDate?.toDate ? data.dueDate.toDate() : (data.dueDate as any)?.toDate?.() || new Date(),
          createdAt: (data.createdAt as any)?.toDate?.() || new Date(),
          sentAt: (data.sentAt as any)?.toDate?.() || undefined,
        } as PaymentReminder);
      });
      
      return reminders.sort((a, b) => b.createdAt.getTime() - a.createdAt.getTime());
    } catch (error) {
      console.error('Error getting reminders for ticket:', error);
      return [];
    }
  }
  
  // Get payment statistics for dashboard
  static async getPaymentStats(): Promise<{
    totalUnpaid: number;
    dueSoon: number;
    overdue: number;
    critical: number;
    totalUnpaidAmount: number;
  }> {
    try {
      // First get all viewer accounts to see which tickets are linked to viewers
      const viewerAccountsQuery = query(collection(db, 'viewerAccounts'));
      const viewerAccountsSnapshot = await getDocs(viewerAccountsQuery);
      
      // Collect all linked ticket codes from all viewer accounts
      const allLinkedTicketCodes = new Set<string>();
      
      viewerAccountsSnapshot.forEach((viewerDoc) => {
        const viewerData = viewerDoc.data();
        const linkedTicketCodes = viewerData.linkedTicketCodes || [];
        
        // Add both formats of each code (with and without TKT- prefix)
        linkedTicketCodes.forEach((code: string) => {
          allLinkedTicketCodes.add(code);
          if (!code.startsWith('TKT-') && code.match(/^\d{4}-/)) {
            allLinkedTicketCodes.add('TKT-' + code);
          }
        });
      });
      
      if (allLinkedTicketCodes.size === 0) {
        return {
          totalUnpaid: 0,
          dueSoon: 0,
          overdue: 0,
          critical: 0,
          totalUnpaidAmount: 0,
        };
      }
      
      // Now get only the unpaid tickets that are linked to viewers
      const now = new Date();
      
      // Firestore 'in' queries are limited to 10 items, so we batch if needed
      const ticketCodeArray = Array.from(allLinkedTicketCodes);
      
      let totalUnpaid = 0;
      let dueSoon = 0;
      let overdue = 0;
      let critical = 0;
      let totalUnpaidAmount = 0;
      
      for (let i = 0; i < ticketCodeArray.length; i += 10) {
        const batch = ticketCodeArray.slice(i, i + 10);
        
        const unpaidTicketsQuery = query(
          collection(db, 'trafficTickets'),
          where('ticketCode', 'in', batch),
          where('status', '==', TicketStatus.ISSUED)
        );
        
        const querySnapshot = await getDocs(unpaidTicketsQuery);
        
        querySnapshot.forEach((doc) => {
          const ticket = doc.data() as TrafficTicket;
          totalUnpaid++;
          totalUnpaidAmount += ticket.fineAmount || 0;
          
          const createdAt = (ticket.createdAt as any)?.toDate?.() || new Date();
          const daysSinceIssued = Math.floor(
            (now.getTime() - createdAt.getTime()) / (1000 * 60 * 60 * 24)
          );
          
          if (daysSinceIssued >= 25 && daysSinceIssued < 30) {
            dueSoon++;
          } else if (daysSinceIssued >= 30 && daysSinceIssued < 45) {
            overdue++;
          } else if (daysSinceIssued >= 45) {
            critical++;
          }
        });
      }
      
      return {
        totalUnpaid,
        dueSoon,
        overdue,
        critical,
        totalUnpaidAmount,
      };
    } catch (error) {
      console.error('Error getting payment stats:', error);
      return {
        totalUnpaid: 0,
        dueSoon: 0,
        overdue: 0,
        critical: 0,
        totalUnpaidAmount: 0,
      };
    }
  }
}
